/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import { CalculatorResult } from '../calculator/calculatorTypes';
import CalculatorConditions, { GasType, GasProperties } from '../calculator/CalculatorConditions';
import { Quantity, Unit } from './unitTypes';
import * as calc from '../calculator/CalculatorFunctions';

class UnitConverter {
  public convert(quantity: Quantity, value: number, from: Unit, to: Unit, currentResult: CalculatorResult, isTolerance: boolean) {
    if (from === to) {
      return value;
    }
    switch (from) {
      // TEMPERATURE
      case '°C':
        switch (to) {
          case '°F':
            return this.cToF(value, isTolerance);
          case 'K':
            return this.cToK(value, isTolerance);
        }
        break;
      case '°F':
        return this.fToC(value, isTolerance);
      case 'K':
        return this.kToC(value, isTolerance);

      // PRESSURE
      case 'hPa':
        switch (to) {
          case 'Pa':
            return this.hpaToPa(value);
          case 'mbar':
            return this.hpaToMbar(value);
          case 'kPa':
            return this.hpaToKpa(value);
          case 'mPa':
            return this.hpaToMpa(value);
          case 'bara':
            return this.hpaToBara(value);
          case 'barg':
            return this.hpaToBarg(value, isTolerance);
          case 'atm':
            return this.hpaToAtm(value);
          case 'mmH2O':
            return this.hpaToMmh2O(value);
          case 'inH2O':
            return this.hpaToInh2O(value);
          case 'psia':
            return this.hpaToPsia(value);
          case 'psig':
            return this.hpaToPsig(value, isTolerance);
          case 'mmHg':
            return this.hpaToMmhgtorr(value);
          case 'torr':
            return this.hpaToMmhgtorr(value);
          case 'inHg':
            return this.hpaToInhg(value);
          case 'm':
            return this.hpaToMeters(value);
          case 'ft':
            return this.hpaToFeets(value);
        }
        break;
      case 'Pa':
        return this.paToHpa(value);
      case 'mbar':
        return this.mbarToHpa(value);
      case 'kPa':
        return this.kpaToHpa(value);
      case 'mPa':
        return this.mpaToHpa(value);
      case 'bara':
        return this.baraToHpa(value);
      case 'barg':
        return this.bargToHpa(value, isTolerance);
      case 'atm':
        return this.atmToHpa(value);
      case 'mmH2O':
        return this.mmh2OToHpa(value);
      case 'inH2O':
        return this.inh2OToHpa(value);
      case 'psia':
        return this.psiaToHpa(value);
      case 'psig':
        return this.psigToHpa(value, isTolerance);
      case 'mmHg':
        return this.mmhgtorrToHpa(value);
      case 'torr':
        return this.mmhgtorrToHpa(value);
      case 'inHg':
        return this.inhgToHpa(value);
      case 'm':
        switch (to) {
          case 'hPa':
            return this.metersToHpa(value);
          case 'ft':
            return this.metersToFt(value);
        }
        break;
      case 'ft':
        switch (to) {
          case 'hPa':
            return this.feetsToHpa(value);
          case 'm':
            return this.feetsToMeters(value);
        }
        break;

      // ENTHALPY
      case 'kJ/kg':
        return this.kjkgToBtulb(value, currentResult, isTolerance);
      case 'BTU/lb':
        return this.btulbToKjkg(value, currentResult, isTolerance);

      // ABSOLUTE HUMIDITY
      case 'g/m3':
        return this.gm3ToGrft3(value);
      case 'gr/ft3':
        return this.grft3ToGm3(value);
      case 'kg/m3':
        return this.kgm3ToLbft3(value);
      case 'lb/ft3':
        return this.lbft3ToKgm3(value);
      case 'm3/kg':
        return this.m3kgToFt3lb(value);
      case 'ft3/lb':
        return this.ft3lbToM3kg(value);

      // MIXING RATIO
      case 'g/kg':
        switch (to) {
          case 'gr/lb':
            return this.gkgToGrlb(value);
          case 'ppmw':
            return this.gkgToPpmw(value);
        }
        break;
      case 'gr/lb':
        return this.grlbToGkg(value);
      case 'ppmw':
        return this.ppmwToGkg(value);

      // VOLUME FRACTION
      case 'ppmvol':
        switch (to) {
          case 'vol-%':
            return this.ppmvolToVolPerc(value);
          case 'umol/mol':
            return value;
        }
        break;
      case 'vol-%':
        return this.volPercToPpmvol(value);
    }

    throw new Error(`UnitConverter.convert(${value}, ${quantity}): undefined conversion ${from} --> ${to})`);
    // }
  }

  // TEMPERATURE
  private cToF = (val: number, tol: boolean) => (tol ? (val * 9) / 5 : (val * 9) / 5 + 32);
  private fToC = (val: number, tol: boolean) => (tol ? (val * 5) / 9 : ((val - 32) * 5) / 9);
  private cToK = (val: number, tol: boolean) => (tol ? val : val + 273.15);
  private kToC = (val: number, tol: boolean) => (tol ? val : val - 273.15);

  // PRESSURE
  private hpaToPa = (val: number) => val * 100;
  private hpaToMbar = (val: number) => val;
  private hpaToKpa = (val: number) => val / 10;
  private hpaToMpa = (val: number) => val / 10000;
  private hpaToBara = (val: number) => val / 1000;
  private hpaToBarg = (val: number, tol: boolean) => (tol ? val / 1000 : (val - 1013.25) / 1000);
  private hpaToAtm = (val: number) => val / 1013.25;
  private hpaToMmh2O = (val: number) => val * 0.7500637554;
  private hpaToInh2O = (val: number) => val / 2.4884;
  private hpaToPsia = (val: number) => val / 68.947572932;
  private hpaToPsig = (val: number, tol: boolean) => (tol ? val / 68.947572932 : (val - 1013.25) / 68.947572932);
  private hpaToMmhgtorr = (val: number) => val / 1.333223684210526;
  private hpaToInhg = (val: number) => val / 33.86389;
  private hpaToMeters = (val: number) => 44330.76923 * (1 - (val / 1013.25) ** 0.190265992);
  private hpaToFeets = (val: number) => 44330.76923 * (1 - (val / 1013.25) ** 0.190265992) * 3.280839895;

  private paToHpa = (val: number) => val / 100;
  private mbarToHpa = (val: number) => val;
  private kpaToHpa = (val: number) => val * 10;
  private mpaToHpa = (val: number) => val * 10000;
  private baraToHpa = (val: number) => val * 1000;
  private bargToHpa = (val: number, tol: boolean) => (tol ? val * 1000 : val * 1000 + 1013.25);
  private atmToHpa = (val: number) => val * 1013.25;
  private mmh2OToHpa = (val: number) => val / 0.7500637554;
  private inh2OToHpa = (val: number) => val * 2.4884;
  private psiaToHpa = (val: number) => val * 68.947572932;
  private psigToHpa = (val: number, tol: boolean) => (tol ? val * 68.947572932 : val * 68.947572932 + 1013.25);
  private mmhgtorrToHpa = (val: number) => val * 1.333223684210526;
  private inhgToHpa = (val: number) => val * 33.86389;
  private metersToHpa = (val: number) => 1013.25 * (1 - 0.0000225577 * val) ** 5.2558;
  private feetsToHpa = (val: number) => 1013.25 * (1 - 0.0000225577 * (val / 3.280839895)) ** 5.2558;

  private metersToFt = (val: number) => val * 3.280839895;
  private feetsToMeters = (val: number) => val / 3.280839895;

  // ENTHALPY
  private kjkgToBtulb = (val: number, currentResult: CalculatorResult, tol: boolean) => {
    if (tol) {
      return val / 2.324;
    } else {
      const g: GasProperties = CalculatorConditions.propertiesForGas(currentResult.parameters.gasType.value as GasType);
      const hImperial =
        calc.pwToH(
          currentResult.parameters.waterVaporPressure.value,
          currentResult.parameters.temperature.value,
          this.fToC(0, false),
          currentResult.parameters.pressure.value,
          g.molarMass,
          g.specificHeatCapacity
        ) / 2.324;
      return hImperial;
    }
  };
  private btulbToKjkg = (val: number, currentResult: CalculatorResult, tol: boolean) => (tol ? val * 2.324 : currentResult.parameters.enthalpy.value);

  // ABSOLUTE HUMIDITY & AIR DENSITY
  private gm3ToGrft3 = (val: number) => val * 0.43696799965357;
  private grft3ToGm3 = (val: number) => 0.43696799965357 / val;
  private kgm3ToLbft3 = (val: number) => val * 0.062427960591578;
  private lbft3ToKgm3 = (val: number) => 0.062427960591578 / val;
  private m3kgToFt3lb = (val: number) => (val * 1) / 0.062427960591578;
  private ft3lbToM3kg = (val: number) => 1 / 0.062427960591578 / val;

  // MIXING RATIO
  private gkgToGrlb = (val: number) => val / 0.14286;
  private gkgToPpmw = (val: number) => val * 1000;
  private grlbToGkg = (val: number) => val * 0.14286;
  private ppmwToGkg = (val: number) => val / 1000;

  // VOLUME FRACTION
  private ppmvolToVolPerc = (val: number) => val / 10000;
  private volPercToPpmvol = (val: number) => val * 10000;
}

export default UnitConverter;
